import { useState } from "react";
import useSWR from "swr";
import { Download } from "lucide-react";
import { fetcher } from "@/lib/api";
import { fmtNum } from "@/lib/format";
import { INCIDENT, downloadUrl, fmtDMY } from "@/lib/traffic";
import { Kpi, Seg } from "@/components/traffic/Widgets";
import { RANGE_PRESETS, RangePicker, SiteSelect, presetRange } from "@/components/traffic/SiteSelect";
import { DayStack, HourHeatmap, KindDonut, SiteStack } from "@/components/traffic/IncidentCharts";
import { cn } from "@/lib/utils";

const KINDS = [{ value: "ALL", label: "All" }, { value: "wrong_way", label: "Wrong-way" }, { value: "stopped", label: "Stopped" }, { value: "slow_down", label: "Slow-down" }];

export function IncidentBadge({ kind }) {
  const k = INCIDENT[kind] || INCIDENT.other;
  return <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-semibold", k.cls, kind === "wrong_way" && "animate-pulse")} data-testid={`incident-kind-${kind}`}>{k.label}</span>;
}

export default function Incidents() {
  const [preset, setPreset] = useState("7d");
  const [range, setRange] = useState(presetRange("7d"));
  const [start, end] = range;
  const [site, setSite] = useState("ALL");
  const [kind, setKind] = useState("ALL");
  const [page, setPage] = useState(0);
  const pick = (p, r) => { setPreset(p); setRange(r); setPage(0); };
  const q = `start=${start}&end=${end}&site=${site}&kind=${kind}`;
  const { data } = useSWR(`/traffic/incidents?${q}&limit=50&skip=${page * 50}`, fetcher, { refreshInterval: 30000 });
  const peakHour = data?.by_hour?.length ? data.by_hour.indexOf(Math.max(...data.by_hour)) : null;
  const dirs = data?.by_dir || {};
  return (
    <div className="space-y-6" data-testid="incidents-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="eyebrow">Radar-detected incidents</div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight sm:text-4xl">Incidents</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <RangePicker preset={preset} range={range} onChange={pick} testId="incidents-range" presets={RANGE_PRESETS.filter((p) => p.value !== "today")} />
          <SiteSelect value={site} onChange={(v) => { setSite(v); setPage(0); }} testId="incidents-site" />
          <Seg value={kind} onChange={(v) => { setKind(v); setPage(0); }} options={KINDS} testId="incident-event-type-filter" />
          <a href={downloadUrl("/reports/incidents", { start, end, site, kind, format: "xlsx" })} data-testid="export-excel-button"
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90"><Download className="h-3.5 w-3.5" /> Excel</a>
          <a href={downloadUrl("/reports/incidents", { start, end, site, kind, format: "pdf" })} data-testid="export-pdf-button" className="rounded-full border px-3 py-1.5 text-xs font-medium hover:bg-secondary">PDF</a>
          <a href={downloadUrl("/reports/incidents", { start, end, site, kind, format: "csv" })} data-testid="export-csv-button" className="rounded-full border px-3 py-1.5 text-xs font-medium hover:bg-secondary">CSV</a>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi label="Incidents in range" value={fmtNum(data?.total)} testId="kpi-card-incidents-total"
          sub={data?.total ? `NB ${fmtNum(dirs.NB || 0)} · SB ${fmtNum(dirs.SB || 0)}${peakHour != null && data.by_hour[peakHour] ? ` · peak hour ${String(peakHour).padStart(2, "0")}:00` : ""}` : null} />
        <Kpi label="Wrong-way" value={fmtNum(data?.by_kind?.wrong_way || 0)} testId="kpi-card-wrong-way" sub={data?.total ? `${Math.round((100 * (data.by_kind?.wrong_way || 0)) / data.total)}% of incidents` : null} />
        <Kpi label="Stopped vehicle" value={fmtNum(data?.by_kind?.stopped || 0)} testId="kpi-card-stopped" sub={data?.total ? `${Math.round((100 * (data.by_kind?.stopped || 0)) / data.total)}% of incidents` : null} />
        <Kpi label="Slow-down" value={fmtNum(data?.by_kind?.slow_down || 0)} testId="kpi-card-slow-down" sub={data?.total ? `${Math.round((100 * (data.by_kind?.slow_down || 0)) / data.total)}% of incidents` : null} />
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <section className="surface p-6 lg:col-span-2" data-testid="incidents-by-day">
          <h2 className="mb-3 text-base font-semibold">Incidents per day <span className="text-xs font-normal text-muted-foreground">· by type</span></h2>
          <DayStack byDayKind={data?.by_day_kind} start={start} end={end} />
        </section>
        <section className="surface p-6" data-testid="incidents-by-kind">
          <h2 className="mb-3 text-base font-semibold">By type</h2>
          <KindDonut byKind={data?.by_kind} total={data?.total} />
        </section>
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <section className="surface p-6 lg:col-span-2" data-testid="incidents-heatmap-section">
          <h2 className="mb-3 text-base font-semibold">When incidents happen <span className="text-xs font-normal text-muted-foreground">· weekday × hour</span></h2>
          <HourHeatmap matrix={data?.by_dow_hour} />
        </section>
        <section className="surface p-6" data-testid="incidents-by-site">
          <h2 className="mb-3 text-base font-semibold">Hot spots <span className="text-xs font-normal text-muted-foreground">· top radar sites</span></h2>
          <SiteStack bySiteKind={data?.by_site_kind} names={data?.site_names} />
        </section>
      </div>
      <section className="surface overflow-hidden" data-testid="incidents-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
              <tr><th className="px-6 py-2">Time</th><th className="px-3 py-2">Site</th><th className="px-3 py-2">Dir</th><th className="px-3 py-2">Lane</th><th className="px-3 py-2">Type</th><th className="px-3 py-2 text-right">Speed</th><th className="px-6 py-2">Radar description</th></tr>
            </thead>
            <tbody>
              {(data?.rows || []).map((r) => (
                <tr key={r.id} className="border-t border-border/60 hover:bg-secondary/40" data-testid="incident-alert-card">
                  <td className="num px-6 py-2 whitespace-nowrap">{fmtDMY(r.ts.slice(0, 10))} {r.ts.slice(11, 19)}</td>
                  <td className="px-3 py-2 font-medium whitespace-nowrap">{r.name} <span className="num text-xs text-muted-foreground">{r.site}</span></td>
                  <td className="px-3 py-2">{r.dir}</td><td className="px-3 py-2">{r.lane || "—"}</td>
                  <td className="px-3 py-2"><IncidentBadge kind={r.kind} /></td>
                  <td className="num px-3 py-2 text-right">{r.speed ? `${r.speed} km/h` : "—"}</td>
                  <td className="max-w-md truncate px-6 py-2 text-xs text-muted-foreground" title={r.desc}>{r.desc}</td>
                </tr>
              ))}
              {!data?.rows?.length && <tr><td colSpan="7" className="px-6 py-10 text-center text-sm text-muted-foreground">No incidents match these filters.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-border/60 px-6 py-3 text-xs text-muted-foreground">
          <span>{data?.total ? `Showing ${page * 50 + 1}–${Math.min((page + 1) * 50, data.total)} of ${fmtNum(data.total)}` : "No incidents"}</span>
          <div className="flex gap-2">
            <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="rounded-full border px-3 py-1 disabled:opacity-40" data-testid="incidents-prev">Previous</button>
            <button disabled={(page + 1) * 50 >= (data?.total || 0)} onClick={() => setPage((p) => p + 1)} className="rounded-full border px-3 py-1 disabled:opacity-40" data-testid="incidents-next">Next</button>
          </div>
        </div>
      </section>
    </div>
  );
}
